import type { PorZonas } from './types';

const ORDEN: Array<'grande' | 'mediana' | 'pequena'> = ['grande', 'mediana', 'pequena'];

/** Lo que cuesta y lo que dura una selección de zonas, y con qué motivo entra.
    Lo usan el widget (para mostrarlo) y el worker (para reservar el espacio),
    así que la cuenta es una sola y no se pueden ir separando. */
export function cuentaDeZonas(porZonas: PorZonas, escogidas: string[]) {
  const zonas = porZonas.zonas.filter((z) => escogidas.includes(z.id));
  let precio = 0;
  let minutos = porZonas.minutosBase;
  for (const z of zonas) {
    const t = porZonas.tamanos[z.tamano];
    precio += t.precio;
    minutos += t.minutos;
  }
  /* el motivo lo pone la zona más grande: así la agenda de Sikumed queda con la
     categoría que corresponde */
  const mayor = ORDEN.find((t) => zonas.some((z) => z.tamano === t)) ?? 'pequena';
  return { precio, minutos, motivo: porZonas.motivoPorTamano[mayor], zonas };
}

/** Deja la selección coherente: una zona grande se come a las que incluye, y
    cuando están todas las partes, se juntan en la que las agrupa. */
export function acomodarZonas(porZonas: PorZonas, escogidas: string[]): string[] {
  const fuera = new Set(escogidas);

  /* de abajo hacia arriba: si están todas las partes, gana la que las agrupa */
  let cambio = true;
  while (cambio) {
    cambio = false;
    for (const z of porZonas.zonas) {
      if (!z.seArmaCon?.length || fuera.has(z.id)) continue;
      if (z.seArmaCon.every((p) => fuera.has(p))) {
        for (const p of z.seArmaCon) fuera.delete(p);
        fuera.add(z.id);
        cambio = true;
      }
    }
  }

  /* y la que agrupa desmarca lo que ya cubre, incluso de rebote */
  for (const id of [...fuera]) {
    for (const p of cubiertasPor(porZonas, id)) fuera.delete(p);
  }
  return porZonas.zonas.filter((z) => fuera.has(z.id)).map((z) => z.id);
}

/** Todo lo que cubre una zona, incluido lo que cubren las suyas: el tórax
    completo cubre el abdomen, y el abdomen la línea alba. */
function cubiertasPor(porZonas: PorZonas, id: string, vistas = new Set<string>()): string[] {
  if (vistas.has(id)) return [];
  vistas.add(id);
  const zona = porZonas.zonas.find((z) => z.id === id);
  const directas = zona?.incluye ?? [];
  return directas.flatMap((p) => [p, ...cubiertasPor(porZonas, p, vistas)]);
}

/** Las que no se pueden marcar porque ya están cubiertas por otra marcada. */
export function zonasBloqueadas(porZonas: PorZonas, escogidas: string[]): string[] {
  const bloqueadas = new Set<string>();
  for (const id of escogidas) {
    for (const p of cubiertasPor(porZonas, id)) bloqueadas.add(p);
  }
  return [...bloqueadas];
}
